<template>
  <v-timeline align-top dense>
    <v-timeline-item
      v-for="auditLogItem of auditLog"
      :key="auditLogItem.logId"
      :color="getTimelineColor(auditLogItem.operation)"
      :small="true"
    >
      <v-card class="elevation-2">
        <v-card-title class="title pb-0">{{ formatOperation(auditLogItem.operation) }} </v-card-title>
        <v-card-text class="caption">
          by {{ formatDisplayName(auditLogItem) }} on
          {{ formatDate(auditLogItem.created) }}
        </v-card-text>
        <v-card-title class="subtitle pb-0">{{ formatItemDataType(auditLogItem.dataType) }}</v-card-title>
        <v-card-text>
          <v-data-table
            :headers="getHeaders(auditLogItem.operation)"
            :hide-default-footer="true"
            :items="auditLogItem.content"
            class="elevation-0"
            dense
            disable-pagination
          >
            <template v-slot:item="{ item }">
              <tr>
                <td>{{ formatItemKey(auditLogItem.dataType, item.key) }}</td>

                <!-- Old Value column - show for updates and deletes -->
                <td v-if="(auditLogItem.operation === 'update' || auditLogItem.operation === 'delete') && valueIsHtml(auditLogItem.dataType, item.key)">
                  <embedded-html :value="item.oldValue" data-cy="changeHistory-oldValue"></embedded-html>
                </td>
                <td v-else-if="(auditLogItem.operation === 'update' || auditLogItem.operation === 'delete') && valueIsUsername(auditLogItem.dataType, item.key)">
                  {{ formatUsername(item.oldValue) }}
                </td>
                <td
                  v-else-if="
                    (auditLogItem.operation === 'update' || auditLogItem.operation === 'delete') &&
                    isIndividualSetupArray(auditLogItem.dataType, item.key, item.oldValue)
                  "
                  v-html="formatNonHtmlValue(auditLogItem.dataType, item.key, item.oldValue)"
                ></td>
                <td v-else-if="auditLogItem.operation === 'update' || auditLogItem.operation === 'delete'" style="white-space: pre-line">
                  {{ formatNonHtmlValue(auditLogItem.dataType, item.key, item.oldValue) }}
                </td>

                <!-- New Value column - show for creates and updates only (not deletes) -->
                <td v-if="auditLogItem.operation !== 'delete' && valueIsHtml(auditLogItem.dataType, item.key)">
                  <embedded-html :value="item.newValue" data-cy="changeHistory-newValue"></embedded-html>
                </td>
                <td v-else-if="auditLogItem.operation !== 'delete' && valueIsUsername(auditLogItem.dataType, item.key)">
                  {{ formatUsername(item.newValue) }}
                </td>
                <td
                  v-else-if="auditLogItem.operation !== 'delete' && isIndividualSetupArray(auditLogItem.dataType, item.key, item.newValue)"
                  v-html="formatNonHtmlValue(auditLogItem.dataType, item.key, item.newValue)"
                ></td>
                <td v-else-if="auditLogItem.operation !== 'delete'" style="white-space: pre-line">
                  {{ formatNonHtmlValue(auditLogItem.dataType, item.key, item.newValue) }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-timeline-item>
  </v-timeline>
</template>

<script>
import { formatUsername, formatUTCtoPST } from "@/lib/formatters";
import { isIndividualSetupArray, formatIndividualSetupTable } from "@/lib/auditLogFormatters";
import { mapActions, mapGetters, mapState } from "vuex";
import databaseFieldList from "@/store/databaseFieldList";

import EmbeddedHtml from "@/components/embeddedHtml";

export default {
  components: {
    EmbeddedHtml,
  },
  computed: {
    ...mapGetters({
      getDropdownValue: "admin/dropdown/getDropdownValueFromValueList",
      getServiceAreaAttribute: "admin/serviceAreaAttribute/getById",
      userMap: "user/getUserMap",
    }),
    ...mapState({
      auditLogRaw: (state) => state.auditLog.releaseNote,
      dropdownList: (state) => state.admin.dropdown.list,
      templateList: (state) => state.templates.list,
    }),
    /**
     * Transforms audit log entries for display.
     * Create operations are transformed from object format to array format
     * to enable unified rendering with update operations.
     * Only non-empty fields are included in create displays.
     */
    auditLog() {
      if (!this.auditLogRaw) return [];

      // Fields to exclude from create operation display
      const COMPUTED_FIELDS = new Set([
        "buildAnalystDisplayName", // Computed via SQL JOIN, not a real database field
        "qaAnalystDisplayName", // Computed via SQL JOIN, not a real database field
        "application", // Computed via SQL JOIN (dropdown label)
        "primaryActivity", // Computed via SQL JOIN (dropdown label)
        "isCustomStep", // Dead attribute, no longer used
      ]);

      return this.auditLogRaw.map((logEntry) => {
        if (logEntry.operation === "create") {
          // Transform create content from object to array format
          const content = Object.entries(logEntry.content || {})
            .filter(([key, value]) => {
              // Filter out computed fields (displayName fields added via SQL JOIN)
              if (COMPUTED_FIELDS.has(key)) return false;

              // Filter out deprecated fields
              const fieldMetadata = this.databaseFieldList[logEntry.dataType]?.[key];
              if (fieldMetadata?.deprecated) return false;

              // Normalize JSON strings to actual values for empty checks
              let normalizedValue = value;
              if (typeof value === "string") {
                try {
                  normalizedValue = JSON.parse(value);
                } catch {
                  // Not valid JSON, treat as regular string
                  normalizedValue = value;
                }
              }

              // Filter out null, undefined, empty strings, and empty arrays
              if (normalizedValue === null || normalizedValue === undefined || normalizedValue === "") return false;
              if (Array.isArray(normalizedValue) && normalizedValue.length === 0) return false;

              return true;
            })
            .map(([key, value]) => ({
              key,
              oldValue: null, // Always null for creates
              newValue: value,
            }));

          return { ...logEntry, content };
        } else if (logEntry.operation === "delete") {
          // Transform delete content from object to array format
          const content = Object.entries(logEntry.content || {})
            .filter(([key, value]) => {
              // Filter out computed fields
              if (COMPUTED_FIELDS.has(key)) return false;

              // Filter out deprecated fields
              const fieldMetadata = this.databaseFieldList[logEntry.dataType]?.[key];
              if (fieldMetadata?.deprecated) return false;

              // Normalize JSON strings to actual values for empty checks
              let normalizedValue = value;
              if (typeof value === "string") {
                try {
                  normalizedValue = JSON.parse(value);
                } catch {
                  // Not valid JSON, treat as regular string
                  normalizedValue = value;
                }
              }

              // Filter out null, undefined, empty strings, and empty arrays
              if (normalizedValue === null || normalizedValue === undefined || normalizedValue === "") return false;
              if (Array.isArray(normalizedValue) && normalizedValue.length === 0) return false;

              return true;
            })
            .map(([key, value]) => ({
              key,
              oldValue: value, // Show the deleted value
              newValue: null, // Always null for deletes
            }));

          return { ...logEntry, content };
        }
        return logEntry;
      });
    },
  },
  async created() {
    await this.loadAuditLog();
  },
  data() {
    return {
      databaseFieldList,
      headers: [
        { text: "Key", sortable: false, width: "20%" },
        { text: "Old Value", sortable: false, width: "40%" },
        { text: "New Value", sortable: false, width: "40%" },
      ],
    };
  },
  methods: {
    async loadAuditLog() {
      try {
        await Promise.all([this.fetchAuditLog(this.releaseNoteId), this.fetchUserList()]);
      } catch (error) {
        console.error(error);
        this.$notifyError("Error fetching audit log");
      }

      if (!this.dropdownList.length) {
        await this.fetchDropdownList();
      }
    },
    getHeaders(operation) {
      if (operation === "create") {
        return [
          { text: "Key", sortable: false, width: "30%" },
          { text: "Value", sortable: false, width: "70%" },
        ];
      } else if (operation === "delete") {
        return [
          { text: "Key", sortable: false, width: "30%" },
          { text: "Value", sortable: false, width: "70%" },
        ];
      }
      return this.headers; // Default 3-column headers for updates
    },
    formatDate(date) {
      return formatUTCtoPST(date);
    },
    formatDisplayName(auditLogItem) {
      // Use firstName and lastName from backend JOIN if available
      if (auditLogItem.firstName && auditLogItem.lastName) {
        return `${auditLogItem.firstName} ${auditLogItem.lastName}`;
      }
      // Fallback to username if user not found in database
      return auditLogItem.username || "";
    },
    formatItemKey(dataType, itemKey) {
      const label =
        this.databaseFieldList[dataType]?.[itemKey]?.label ?? this.databaseFieldList.releaseNote[itemKey]?.label;
      return label || itemKey;
    },
    formatItemDataType(dataType) {
      if (dataType === "releaseNote") {
        return "Release Note";
      } else if (dataType === "testStep") {
        return "Test Step";
      }
      return dataType;
    },
    formatNonHtmlValue(auditLogItemDataType, itemKey, value) {
      if (value === null || value === undefined) {
        return "";
      }
      // Format boolean values (0 -> false, 1 -> true)
      if (this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "boolean") {
        return value ? "true" : "false";
      }
      if (this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "dropdownValueId") {
        const dropdownValue = this.getDropdownValue(value);
        return dropdownValue.label || value;
      }
      if (this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "templateType") {
        return this.templateList[value]?.name || value;
      }
      if (this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "serviceAreaAttributeList") {
        // handle inconsistency in data formatted for the audit log
        if (typeof value === "string") {
          value = JSON.parse(value);
        }
        if (Array.isArray(value)) {
          const nameList = value.map((itemValue) => {
            const { name } = this.getServiceAreaAttribute(itemValue);
            return name;
          });

          return nameList.join(", ");
        } else {
          return value;
        }
      }
      if (this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "array") {
        // Format arrays nicely (remove brackets and quotes)
        // Handle inconsistency in data format (string vs array)
        if (typeof value === "string") {
          try {
            value = JSON.parse(value);
          } catch {
            // If parsing fails, treat as single value
            return value;
          }
        }
        if (Array.isArray(value)) {
          if (value.length === 0) {
            return "";
          }

          // Special formatting for individualSetup (array of objects) - render as mini-table
          if (itemKey === "individualSetup") {
            return formatIndividualSetupTable(value);
          }

          // Special formatting for jiraSupportAnalyst (array of usernames) - format each username
          if (itemKey === "jiraSupportAnalyst") {
            return value
              .filter((item) => item !== null && item !== undefined)
              .map((username) => this.formatUsername(username))
              .join(", ");
          }

          return value.filter((item) => item !== null && item !== undefined).join(", ");
        }
        return value;
      }
      return value;
    },
    formatOperation(operation) {
      if (operation === "create") {
        return "Created";
      } else if (operation === "update") {
        return "Updated";
      } else if (operation === "delete") {
        return "Deleted";
      }
      return "";
    },
    formatUsername(username) {
      return formatUsername(username, this.userMap);
    },
    getTimelineColor(operation) {
      if (operation === "create") {
        return "green";
      } else if (operation === "delete") {
        return "red";
      }
      return "blue lighten-2";
    },
    valueIsHtml(auditLogItemDataType, itemKey) {
      if (this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "richText") {
        return true;
      }
      return false;
    },
    valueIsUsername(auditLogItemDataType, itemKey) {
      return this.databaseFieldList[`${auditLogItemDataType}`][`${itemKey}`]?.dataType === "user";
    },
    isIndividualSetupArray(auditLogItemDataType, itemKey, value) {
      return isIndividualSetupArray(itemKey, value);
    },
    ...mapActions({
      fetchAuditLog: "auditLog/fetchReleaseNote",
      fetchDropdownList: "admin/dropdown/list",
      fetchUserList: "user/load",
    }),
  },
  props: ["releaseNoteId"],
  watch: {
    // This component is reused when the user moves to a different release note (e.g. the source template link).
    releaseNoteId() {
      return this.loadAuditLog();
    },
  },
};
</script>
